import { IconStar, IconStarFilled } from "@tabler/icons-react";
import { DateTime } from "luxon";
import {
  MantineReactTable,
  type MRT_ColumnDef,
  useMantineReactTable
} from "mantine-react-table-open";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { MultiConfirmationModal } from "../../../components/BuildingBlocks/Layout/Modals/MultiConfirmationModal/MultiConfirmationModal";
import { LoadOrError } from "../../../components/LoadOrError/LoadOrError";
import { NoEntries } from "../../../components/NoEntries/NoEntries";
import { useMeteringOrMarketLocations } from "../../../hooks/useMeteringOrMarketLocations";
import { useVariantSites } from "../../../hooks/useVariantSites";
import { getDefaultMRTOptions } from "../../../mantine/getDefaultMRTOptions";
import { getDefaultMRTRowEditModeOptions } from "../../../mantine/getDefaultMRTRowEditModeOptions";
import { getMRTTopToolbarCustomActionsRenderer } from "../../../mantine/getMRTTopToolbarCustomActionsRenderer";
import { getDateEndColumn } from "../../../mantine/mrt/components/columns/getDateEndColumn";
import { getDateStartColumn } from "../../../mantine/mrt/components/columns/getDateStartColumn";
import { getPersonColumn } from "../../../mantine/mrt/components/columns/getPersonColumn";
import { MRT_TableDetailPanelWrapper } from "../../../mantine/mrt/components/other/MRT_TableDetailPanelWrapper";
import { useToast } from "../../../mantine/notifications/useToast";
import { emptyArray } from "../../../utils/constants";
import { backendDateOrDateTimeToLuxonDateTimeOrNull } from "../../../utils/dates/backendDateOrDateTimeToLuxonDateTimeOrNull";
import { useParagraph6Companies } from "../hooks/useParagraph6Companies";
import { useParagraph6ContractMutations } from "../hooks/useParagraph6ContractMutations";
import { useParagraph6Contracts } from "../hooks/useParagraph6Contracts";
import { useParagraph6CreatableCredits } from "../hooks/useParagraph6CreatableCredits";
import type {
  Paragraph6ContractResponse,
  Paragraph6CreatableCredit
} from "../Paragraph6.types";
import { getBillingColumn } from "./columns/getBillingColumn";
import { getCommunityColumn } from "./columns/getCommunityColumn";
import { getGeneratorsColumn } from "./columns/getGeneratorsColumn";
import { getIsTemplateColumn } from "./columns/getIsTemplateColumn";
import { GenerationShareIncompleteWarning } from "./GenerationShareIncompleteWarning/GenerationShareIncompleteWarning";
import { Paragraph6ContractInformation } from "./Paragraph6ContractInformation/Paragraph6ContractInformation";
import { Paragraph6ContractModals } from "./Paragraph6ContractModals/Paragraph6ContractModals";

interface Paragraph6ContractPageProps {
  variantId: number;
  projectId: string;
}

function Paragraph6ContractPage({
  variantId,
  projectId
}: Paragraph6ContractPageProps) {
  const [newContractModalOpen, setNewContractModalOpen] = useState(false);
  const [newGeneratorModalOpen, setNewGeneratorModalOpen] = useState(false);
  const [modalContractId, setModalContractId] = useState<string | null>(null);
  const [contractToDelete, setContractToDelete] =
    useState<Paragraph6ContractResponse | null>(null);

  const {
    paragraph6Contracts,
    isLoading: paragraph6ContractsLoading,
    error: paragraph6ContractsError
  } = useParagraph6Contracts(variantId);
  const { creatableCredits, error: creatableCreditsError } =
    useParagraph6CreatableCredits(variantId);

  const { paragraph6Companies } = useParagraph6Companies(variantId);
  const { data: meteringOrMarketLocations } = useMeteringOrMarketLocations({
    variantId
  });

  const { paragraph6ContractEditMutation, paragraph6ContractDeleteMutation } =
    useParagraph6ContractMutations(variantId);

  const { sites } = useVariantSites(variantId);

  const navigate = useNavigate();
  const { showToast } = useToast();

  async function deleteContract() {
    if (!contractToDelete) {
      return;
    }

    const deletePromise = paragraph6ContractDeleteMutation.mutateAsync(
      contractToDelete.id,
      {
        onError: (error) => {
          showToast({
            type: "error",
            message: error,
            autoClose: false,
            withCloseButton: true
          });
        },
        onSettled: () => {
          setContractToDelete(null);
        }
      }
    );

    return Promise.all([deletePromise])
      .then(() => {})
      .catch(() => {
        /* Error message is created per mutation in the mutation's onError */
      });
  }

  function handleClickNewGeneratorShare(contractId: string) {
    setModalContractId(contractId);
    setNewGeneratorModalOpen(true);
  }

  function handleClickEditGeneratorShare(
    generatorShareId: number,
    contractId: string
  ) {
    setModalContractId(contractId);
    navigate(`erzeuger-bearbeiten/${generatorShareId}`);
  }

  function toggleContractTemplate(contract: Paragraph6ContractResponse) {
    contract.is_template = !contract.is_template;
    paragraph6ContractEditMutation.mutateAsync(contract);
  }

  const columns: Array<MRT_ColumnDef<Paragraph6ContractResponse>> =
    useMemo(() => {
      const creditsByContractId = creatableCreditsError
        ? new Map<string, Array<Paragraph6CreatableCredit>>()
        : creatableCredits?.reduce((map, credit) => {
            const list = map.get(credit.contract) ?? [];
            list.push(credit);
            map.set(credit.contract, list);
            return map;
          }, new Map<string, Array<Paragraph6CreatableCredit>>());

      return [
        {
          header: "Vertragsname",
          accessorKey: "name",
          size: 240,
          grow: false,
          enableCellHoverReveal: true,
          enableColumnFilter: false
        },
        getCommunityColumn(),
        getPersonColumn<Paragraph6ContractResponse>({
          key: "supplier",
          options: {
            header: "Betreibergesellschaft",
            size: 240,
            grow: false,
            enableCellHoverReveal: true
          },
          persons: paragraph6Companies || []
        }),
        getDateStartColumn<Paragraph6ContractResponse>({
          key: "begin",
          options: {
            header: "Vertragsbeginn"
          }
        }),
        getDateEndColumn<Paragraph6ContractResponse>({
          key: "end",
          options: {
            header: "Vertragsende"
          },
          showExpiringSoonIndicator: true,
          expiringSoonThresholdDuration: { months: 3, weeks: 2 },
          getExpiringSoonOrExpiredTooltipText: (contract) => {
            const end = backendDateOrDateTimeToLuxonDateTimeOrNull(
              contract.end
            );
            if (end && end < DateTime.now()) {
              return "Der Vertrag ist abgelaufen";
            }
            return "Der Vertrag läuft bald ab";
          }
        }),
        getBillingColumn({ credits: creditsByContractId }),
        getGeneratorsColumn(),
        getIsTemplateColumn()
      ];
    }, [paragraph6Companies, creatableCredits, creatableCreditsError]);

  const mrtTable = useMantineReactTable<Paragraph6ContractResponse>({
    ...getDefaultMRTOptions({
      initialState: {
        showGlobalFilter: true
      },
      hideBorder: true
    }),
    layoutMode: "grid",
    ...getMRTTopToolbarCustomActionsRenderer({
      create: {
        text: "Vertrag hinzufügen",
        onClick: () => setNewContractModalOpen(true)
      },
      delete: { hide: true }
    }),
    ...getDefaultMRTRowEditModeOptions({
      rowActions: {
        other: [
          {
            text: "Referenzvertrag?",
            dynamicIcon: (rowData) =>
              rowData.is_template ? IconStarFilled : IconStar,
            onClick: (row) => toggleContractTemplate(row.original)
          }
        ],
        edit: {
          text: "Vertrag bearbeiten",
          onClick: (row) => {
            mrtTable.setEditingRow(null);
            navigate(row.original.id);
          }
        },
        delete: {
          text: "Vertrag löschen",
          onClick: (row) => setContractToDelete(row.original)
        }
      },
      displayColumnDefOptions: {
        "mrt-row-actions": {
          size: 100,
          grow: false
        }
      }
    }),
    renderDetailPanel: ({ row }) => {
      if (
        !row.original ||
        (typeof row.original === "object" &&
          Object.keys(row.original).length === 0) ||
        !meteringOrMarketLocations
      ) {
        return;
      }
      return (
        <MRT_TableDetailPanelWrapper>
          <Paragraph6ContractInformation
            contract={row.original}
            meteringOrMarketLocations={meteringOrMarketLocations}
            onClickEditGeneratorShare={(generatorShareId) =>
              handleClickEditGeneratorShare(generatorShareId, row.original.id)
            }
            onClickNewGeneratorShare={handleClickNewGeneratorShare}
          />
        </MRT_TableDetailPanelWrapper>
      );
    },
    enableRowSelection: false,
    enableFilterMatchHighlighting: true,
    enableColumnFilters: true,
    columnFilterDisplayMode: "popover",
    enableFacetedValues: true,
    state: {
      isLoading: paragraph6ContractsLoading
    },
    columns,
    data: paragraph6Contracts ?? emptyArray
  });

  return (
    <div className="Paragraph6ContractPage">
      {paragraph6Companies &&
        sites.map((site) => (
          <GenerationShareIncompleteWarning
            companies={paragraph6Companies}
            key={site.id}
            site={site}
          />
        ))}

      <LoadOrError error={paragraph6ContractsError}>
        {(paragraph6Contracts && paragraph6Contracts.length > 0) ||
        paragraph6ContractsLoading ? (
          <>
            <MantineReactTable table={mrtTable} />
            <MultiConfirmationModal
              actionName="löschen"
              actionObjects={contractToDelete ? [contractToDelete.name] : []}
              confirmationText="Ihr Vertrag kann anschließend nicht wiederhergestellt werden."
              headerText="Wollen Sie diesen Vertrag löschen?"
              isModalOpen={!!contractToDelete}
              objectName="Vertrag"
              toggleModal={() => setContractToDelete(null)}
              onAction={deleteContract}
            />
          </>
        ) : (
          <NoEntries
            entryType="Vertrag"
            onClickAddEntry={() => setNewContractModalOpen(true)}
          />
        )}

        <Paragraph6ContractModals
          contractId={modalContractId}
          newContractModalOpen={newContractModalOpen}
          newGeneratorModalOpen={newGeneratorModalOpen}
          projectId={projectId}
          templates={paragraph6Contracts?.filter(
            (contract) => contract.is_template
          )}
          variantId={variantId}
          onNewContractModalClose={(contractId) => {
            if (contractId) mrtTable.setExpanded({ [contractId]: true });
            setNewContractModalOpen(false);
          }}
          onNewGeneratorModalClose={() => setNewGeneratorModalOpen(false)}
        />
      </LoadOrError>
    </div>
  );
}

export { Paragraph6ContractPage, Paragraph6ContractPageProps };
